"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.BaseContentManagementQueryService = void 0;
var kontent_core_1 = require("@kentico/kontent-core");
var rxjs_1 = require("rxjs");
var operators_1 = require("rxjs/operators");
var models_1 = require("../models");
var BaseContentManagementQueryService = /** @class */ (function () {
    function BaseContentManagementQueryService(config, httpService, sdkInfo) {
        this.config = config;
        this.httpService = httpService;
        this.sdkInfo = sdkInfo;
        /**
         * Default base url for content management API
         */
        this.defaultBaseCMUrl = 'https://manage.kontent.ai/v2/projects';
    }
    /**
     * Gets url based on the action, query configuration and options (parameters)
     * @param action Action (= url part) that will be hit
     * @param options Query options
     * @param addSlash Indicates if slash is added to query
     */
    BaseContentManagementQueryService.prototype.getFullUrl = function (action, options, addSlash) {
        if (addSlash === void 0) { addSlash = true; }
        return kontent_core_1.urlHelper.addOptionsToUrl(this.getBaseUrl() + (addSlash ? '/' : '') + action, options);
    };
    /**
     * Gets proper set of headers for given request.
     * @param config Query config
     */
    BaseContentManagementQueryService.prototype.getHeaders = function (config) {
        var headers = [
            // sdk tracking header
            kontent_core_1.headerHelper.getSdkIdHeader({
                host: this.sdkInfo.host,
                name: this.sdkInfo.name,
                version: this.sdkInfo.version
            }),
            // add authorization header
            this.getAuthorizationHeader(this.config.apiKey)
        ];
        // add query headers
        headers.push.apply(headers, config.headers);
        return headers;
    };
    /**
     * Http PATCH response
     * @param url Url of request
     * @param config Query configuration
     */
    BaseContentManagementQueryService.prototype.patchResponse = function (url, body, internalConfig, config) {
        var _this = this;
        return this.httpService
            .patch({
            url: url,
            body: body
        }, {
            retryStrategy: this.config.retryStrategy,
            headers: this.getHeaders(config),
            logErrorToConsole: this.config.isDeveloperMode,
            responseType: internalConfig && internalConfig.responseType ? internalConfig.responseType : undefined
        })
            .pipe(operators_1.catchError(function (error) {
            return rxjs_1.throwError(_this.mapContentManagementError(error));
        }));
    };
    /**
     * Http GET response
     * @param url Url of request
     * @param config Query configuration
     */
    BaseContentManagementQueryService.prototype.getResponse = function (url, internalConfig, config) {
        var _this = this;
        return this.httpService
            .get({
            url: url,
        }, {
            retryStrategy: this.config.retryStrategy,
            headers: this.getHeaders(config),
            logErrorToConsole: this.config.isDeveloperMode,
            responseType: internalConfig && internalConfig.responseType ? internalConfig.responseType : undefined
        })
            .pipe(operators_1.catchError(function (error) {
            return rxjs_1.throwError(_this.mapContentManagementError(error));
        }));
    };
    /**
     * Http POST response
     * @param url Url of request
     * @param body Body of the request (names and values)
     * @param config Query configuration
     */
    BaseContentManagementQueryService.prototype.postResponse = function (url, body, internalConfig, config) {
        var _this = this;
        return this.httpService
            .post({
            url: url,
            body: body,
        }, {
            retryStrategy: this.config.retryStrategy,
            headers: this.getHeaders(config),
            logErrorToConsole: this.config.isDeveloperMode,
            responseType: internalConfig && internalConfig.responseType ? internalConfig.responseType : undefined
        })
            .pipe(operators_1.catchError(function (error) {
            return rxjs_1.throwError(_this.mapContentManagementError(error));
        }));
    };
    /**
     * Http PUT response
     * @param url Url of request
     * @param body Body of the request (names and values)
     * @param config Query configuration
     */
    BaseContentManagementQueryService.prototype.putResponse = function (url, body, internalConfig, config) {
        var _this = this;
        return this.httpService
            .put({
            url: url,
            body: body,
        }, {
            retryStrategy: this.config.retryStrategy,
            headers: this.getHeaders(config),
            logErrorToConsole: this.config.isDeveloperMode,
            responseType: internalConfig && internalConfig.responseType ? internalConfig.responseType : undefined
        })
            .pipe(operators_1.catchError(function (error) {
            return rxjs_1.throwError(_this.mapContentManagementError(error));
        }));
    };
    /**
     * Http Delete response
     * @param url Url of request
     * @param body Body of the request (names and values)
     * @param config Query configuration
     */
    BaseContentManagementQueryService.prototype.deleteResponse = function (url, internalConfig, config) {
        var _this = this;
        return this.httpService
            .delete({
            url: url,
        }, {
            retryStrategy: this.config.retryStrategy,
            headers: this.getHeaders(config),
            logErrorToConsole: this.config.isDeveloperMode,
            responseType: internalConfig && internalConfig.responseType ? internalConfig.responseType : undefined
        })
            .pipe(operators_1.catchError(function (error) {
            return rxjs_1.throwError(_this.mapContentManagementError(error));
        }));
    };
    BaseContentManagementQueryService.prototype.mapContentManagementError = function (error) {
        var _a;
        var axiosError;
        if (error.error) {
            axiosError = error.error;
        }
        else {
            axiosError = error;
        }
        if (!axiosError || !axiosError.isAxiosError) {
            return error;
        }
        var cmError = (_a = axiosError.response) === null || _a === void 0 ? void 0 : _a.data;
        if (!cmError || !cmError.error_code) {
            return error;
        }
        var validationErrors = [];
        if (cmError.validation_errors) {
            validationErrors.push.apply(validationErrors, cmError.validation_errors.map(function (validationErrorRaw) { return new models_1.SharedModels.ValidationError({
                message: validationErrorRaw.message
            }); }));
        }
        return new models_1.SharedModels.ContentManagementBaseKontentError({
            errorCode: cmError.error_code,
            message: cmError.message,
            originalError: error,
            requestId: cmError.request_id,
            validationErrors: validationErrors
        });
    };
    /**
     * Gets authorization header. This is used for 'preview' functionality
     */
    BaseContentManagementQueryService.prototype.getAuthorizationHeader = function (key) {
        if (!key) {
            throw Error("Cannot get authorization header because key is undefined");
        }
        // authorization header required for preview mode
        return {
            header: 'authorization',
            value: "bearer " + key
        };
    };
    /**
     * Gets base URL of the request including the project Id
     */
    BaseContentManagementQueryService.prototype.getBaseUrl = function () {
        return this.GetEndpointUrl() + '/' + this.config.projectId;
    };
    /**
     * Gets API endpoint url
     */
    BaseContentManagementQueryService.prototype.GetEndpointUrl = function () {
        if (this.config.baseUrl) {
            return this.config.baseUrl;
        }
        return this.defaultBaseCMUrl;
    };
    return BaseContentManagementQueryService;
}());
exports.BaseContentManagementQueryService = BaseContentManagementQueryService;
//# sourceMappingURL=base-content-management-service.class.js.map